import type { LegalDocPair } from '../types'

/**
 * Términos de ventas y pagos.
 *
 * Respalda lo que el servidor hace al vender desde un Sitio: cada cobro es un
 * cargo directo en la cuenta de Stripe del vendedor (él es el comerciante, no
 * véltica), véltica retiene una comisión por venta según el plan del dueño del
 * sitio, y esa comisión se devuelve en proporción cuando la venta se reembolsa o
 * se pierde una disputa. Las cifras salen de `PLATFORM_FEE_PERCENT` a través de
 * los marcadores, para que estos términos y el cobro no puedan divergir.
 */
export const SELLERS: LegalDocPair = {
  en: {
    title: 'Seller Terms',
    summary: 'The rules for selling products and services and taking payments through your Site, including our fee.',
    intro: [
      'These Seller Terms are part of the [Terms of Service](doc:terms). They apply if you connect a Stripe account and sell products or services, take bookings or accept payments through a Site built on véltica.',
    ],
    sections: [
      {
        title: 'How selling works',
        subs: [
          {
            title: 'You are the seller',
            blocks: [
              'When you sell through a Site, the sale is between you and your buyer. You are the seller and the merchant of record. véltica is not a party to the sale, is not a marketplace, and does not sell, warrant or deliver what you offer.',
            ],
          },
          {
            title: 'Stripe',
            blocks: [
              'Payments are processed by Stripe, an independent company. To sell, you must connect a Stripe account and accept Stripe’s terms, including its connected account agreement, and complete the identity and business verification Stripe requires. Each payment is charged directly on your Stripe account, and your Stripe account is what receives the money and pays it out to your bank according to Stripe’s terms. véltica does not hold, receive or transmit your funds. Stripe’s terms govern anything to do with your account, verification, payouts and its fees.',
            ],
          },
          {
            title: 'Restricted businesses',
            blocks: [
              'You may not sell anything that our [Acceptable Use Policy](doc:aup) or Stripe’s list of prohibited and restricted businesses does not allow. We or Stripe may disable selling on your Sites if you do.',
            ],
          },
          {
            title: 'Emails to your buyers',
            blocks: [
              'On your behalf, we send your buyers and clients the transactional emails of their purchase or booking: the order confirmation with its order number, shipping or pickup notices, refund notices, and booking confirmations, reminders and changes, including when you cancel or move a booking from your dashboard. They show your business name as the sender, come from our sending address, and replies go to you. Order emails include a private link where the buyer can check the status of their order. These emails do not replace the notices the law may require you to give your buyers.',
            ],
          },
        ],
      },
      {
        title: 'Your responsibilities as a seller',
        blocks: [
          'You are responsible for:',
          {
            list: [
              'the accuracy of your listings, prices, descriptions, availability and the terms of what you sell;',
              'delivering what you sell, on time and as described, and providing support to your buyers;',
              'for physical products, packing and shipping them or handing them over, the shipping options and costs you offer, keeping your stock up to date, handling lost or damaged packages, and showing a clear shipping and returns policy. véltica does not ship, store or insure anything you sell, and tracking numbers you enter are passed on to your buyer as you provide them;',
              'showing your buyers a clear refund, cancellation and withdrawal policy, and following the consumer laws that apply to you and to them, including any right to cancel and rules on subscriptions and automatic renewals;',
              'having the rights and licenses to what you sell, including digital products and their delivery;',
              'the taxes on your sales, including registering, collecting and paying sales tax, VAT or similar taxes where required. véltica is not a marketplace facilitator, does not collect or pay taxes on your sales, and does not give tax advice. Stripe offers an optional tax tool that you may enable yourself;',
              'your buyers’ personal data, which you control and we process for you under the [Data Processing Addendum](doc:dpa), and giving your buyers the privacy notices the law requires; and',
              'any discount codes and promotions you create.',
            ],
          },
        ],
      },
      {
        title: 'Our fee on your sales',
        subs: [
          {
            title: 'The fee',
            blocks: [
              'On each sale paid through véltica we keep a platform fee that depends on the plan of the Account that owns the Site at the moment of the sale:',
              {
                table: {
                  head: ['Plan', 'Fee per sale'],
                  rows: [
                    ['{planStarter}', '{feeStarter}%'],
                    ['{planPro}', '{feePro}%'],
                    ['{planBusiness}', '{feeBusiness}%'],
                  ],
                },
              },
            ],
          },
          {
            title: 'How it is calculated',
            blocks: [
              'The fee is calculated on the price before taxes and shipping and is withheld automatically when the payment is made, so you receive the rest in your Stripe account. If a sale includes a discount code, the fee may be calculated on a lower amount, never on a higher one. For subscriptions and other recurring sales, it is applied to each charge. The fee is separate from, and in addition to, the fees Stripe charges you for processing the payment, which you pay to Stripe under your agreement with it.',
            ],
          },
          {
            title: 'We return our fee when a sale is returned',
            blocks: [
              'If you refund a sale, in whole or in part, we return our fee in the same proportion. If you lose a dispute over a sale, we also return our fee for that sale. A dispute that is open or that you win does not change the fee. We do not return the fees Stripe charges; those are governed by Stripe’s terms.',
            ],
          },
          {
            title: 'Changes to the fee',
            blocks: [
              'We may change the fees. We will tell you at least 30 days before a change takes effect, and it will apply only to sales made after that date.',
            ],
          },
        ],
      },
      {
        title: 'Refunds and disputes',
        subs: [
          {
            title: 'Refunds',
            blocks: [
              'You issue refunds to your buyers from your Stripe account, at your discretion and in line with the policy you show them and the law. The refunded amount comes out of your account, and Stripe does not return its processing fee on a refund. When Stripe reports a refund, we email the buyer a notice with the amount refunded.',
            ],
          },
          {
            title: 'Disputes and chargebacks',
            blocks: [
              'When a buyer disputes a payment with their bank, the amount and the dispute fee are debited from your Stripe account. You are responsible for answering disputes with the evidence Stripe requests within the time it gives you, which can be as short as a week. When we learn of a new dispute, we show a notice in your dashboard and email you the amount and, when Stripe gives it, the deadline to respond, even if you turned off sale notices. We cannot answer for you and we are not liable if a dispute is lost, including if a notice does not reach you.',
            ],
          },
          {
            title: 'Negative balances',
            blocks: [
              'If refunds or disputes leave your Stripe account with a negative balance, that is governed by your agreement with Stripe. You are responsible for any amount you owe Stripe or any buyer.',
            ],
          },
          {
            title: 'Excessive refunds or disputes',
            blocks: [
              'If your Sites generate an unusually high rate of refunds, disputes or complaints, we or Stripe may ask you for information, limit or disable selling on your Sites, or terminate your Account.',
            ],
          },
        ],
      },
      {
        title: 'Digital products, services and bookings',
        subs: [
          {
            title: 'Digital products',
            blocks: [
              'You are responsible for the content of the digital products you sell and for having the right to sell them. We make them available to your buyers through the Platform, but we do not guarantee delivery beyond the normal operation of the Platform, and we are not responsible for how buyers use what they download.',
            ],
          },
          {
            title: 'Services and bookings',
            blocks: [
              'If you offer services or bookings, you set your availability, prices, deposits and cancellation and no-show policies, and you are responsible for providing the service and for refunds owed to your customers. On your behalf, we may send booking confirmations and reminders and, if you connect it, create events in your Google Calendar. Bookings are your commitment to your customer, not ours.',
            ],
          },
          {
            title: 'Recurring sales',
            blocks: [
              'If you sell subscriptions or other recurring charges, you are responsible for clearly disclosing the terms, getting any authorization the law requires, sending any required renewal or cancellation notices, and offering an easy way to cancel.',
            ],
          },
        ],
      },
      {
        title: 'Suspension and ending',
        blocks: [
          'We may disable checkout on your Sites at any time if we believe you are violating these terms, the Terms of Service or the law, if there is a risk for buyers or the Platform, or if Stripe asks us to. You may disconnect Stripe at any time; existing transactions, receipts and payouts stay in your Stripe account and remain governed by your agreement with Stripe. Disconnecting does not cancel refunds or disputes already in progress or your obligations to your buyers.',
        ],
      },
      {
        title: 'Liability',
        blocks: [
          'We are not liable for Stripe’s services, for holds or delays in your payouts, for the acts or omissions of your buyers, or for losses from your sales, refunds or disputes. The disclaimers, limits of liability and indemnification in the Terms of Service apply to everything in these Seller Terms.',
        ],
      },
    ],
  },

  es: {
    title: 'Términos de ventas y pagos',
    summary: 'Las reglas para vender productos y servicios y cobrar desde tu Sitio, incluida nuestra comisión.',
    intro: [
      'Estos Términos de ventas y pagos forman parte de los [Términos de servicio](doc:terms). Aplican si conectas una cuenta de Stripe y vendes productos o servicios, tomas reservas o aceptas pagos desde un Sitio creado en véltica.',
    ],
    sections: [
      {
        title: 'Cómo funciona vender',
        subs: [
          {
            title: 'Tú eres el vendedor',
            blocks: [
              'Cuando vendes desde un Sitio, la venta es entre tú y tu comprador. Tú eres el vendedor y el comerciante responsable. véltica no es parte de la venta, no es un mercado y no vende, garantiza ni entrega lo que ofreces.',
            ],
          },
          {
            title: 'Stripe',
            blocks: [
              'Los pagos los procesa Stripe, una empresa independiente. Para vender debes conectar una cuenta de Stripe y aceptar sus términos, incluido su acuerdo de cuenta conectada, y completar la verificación de identidad y de negocio que Stripe exija. Cada pago se cobra directamente en tu cuenta de Stripe, y tu cuenta de Stripe es la que recibe el dinero y lo transfiere a tu banco según los términos de Stripe. véltica no retiene, recibe ni transmite tus fondos. Los términos de Stripe rigen todo lo relativo a tu cuenta, la verificación, los pagos a tu banco y sus tarifas.',
            ],
          },
          {
            title: 'Negocios restringidos',
            blocks: [
              'No puedes vender nada que nuestra [Política de uso aceptable](doc:aup) o la lista de negocios prohibidos y restringidos de Stripe no permitan. Nosotros o Stripe podemos desactivar las ventas en tus Sitios si lo haces.',
            ],
          },
          {
            title: 'Correos a tus compradores',
            blocks: [
              'En tu nombre, enviamos a tus compradores y clientes los correos transaccionales de su compra o reserva: la confirmación del pedido con su número, los avisos de envío o de recogida, los avisos de reembolso, y las confirmaciones, recordatorios y cambios de reservas, incluso cuando cancelas o mueves una reserva desde tu panel. Llevan el nombre de tu negocio como remitente, salen de nuestra dirección de envío y las respuestas te llegan a ti. Los correos de pedido incluyen un enlace privado donde el comprador puede consultar el estado de su pedido. Estos correos no sustituyen los avisos que la ley te obligue a dar a tus compradores.',
            ],
          },
        ],
      },
      {
        title: 'Tus responsabilidades como vendedor',
        blocks: [
          'Eres responsable de:',
          {
            list: [
              'la exactitud de tus fichas, precios, descripciones, disponibilidad y de las condiciones de lo que vendes;',
              'entregar lo que vendes, a tiempo y como se describe, y dar soporte a tus compradores;',
              'en los productos físicos, empacarlos y enviarlos o entregarlos, las formas y costos de envío que ofreces, mantener al día tus existencias, atender los paquetes perdidos o dañados y mostrar una política clara de envíos y devoluciones. véltica no envía, almacena ni asegura nada de lo que vendes, y los números de guía que escribes se le pasan a tu comprador tal como los das;',
              'mostrar a tus compradores una política clara de reembolsos, cancelación y desistimiento, y cumplir las leyes de consumo que te aplican a ti y a ellos, incluido cualquier derecho de cancelación y las reglas sobre suscripciones y renovaciones automáticas;',
              'tener los derechos y licencias de lo que vendes, incluidos los productos digitales y su entrega;',
              'los impuestos de tus ventas, incluido registrarte, cobrar y pagar el impuesto sobre ventas, el IVA u otros similares cuando corresponda. véltica no es facilitador de mercado, no cobra ni paga impuestos por tus ventas y no da asesoría fiscal. Stripe ofrece una herramienta fiscal opcional que puedes activar tú;',
              'los datos personales de tus compradores, que controlas tú y que nosotros tratamos por tu encargo conforme al [Acuerdo de procesamiento de datos](doc:dpa), y dar a tus compradores los avisos de privacidad que exige la ley; y',
              'los códigos de descuento y promociones que crees.',
            ],
          },
        ],
      },
      {
        title: 'Nuestra comisión sobre tus ventas',
        subs: [
          {
            title: 'La comisión',
            blocks: [
              'En cada venta pagada a través de véltica retenemos una comisión de plataforma que depende del plan de la Cuenta dueña del Sitio en el momento de la venta:',
              {
                table: {
                  head: ['Plan', 'Comisión por venta'],
                  rows: [
                    ['{planStarter}', '{feeStarter}%'],
                    ['{planPro}', '{feePro}%'],
                    ['{planBusiness}', '{feeBusiness}%'],
                  ],
                },
              },
            ],
          },
          {
            title: 'Cómo se calcula',
            blocks: [
              'La comisión se calcula sobre el precio antes de impuestos y de envío, y se retiene automáticamente al hacerse el pago, de modo que recibes el resto en tu cuenta de Stripe. Si la venta lleva un código de descuento, la comisión puede calcularse sobre un importe menor, nunca sobre uno mayor. En las suscripciones y otras ventas recurrentes, se aplica a cada cobro. La comisión es independiente de las tarifas que Stripe te cobra por procesar el pago, y se suma a ellas; esas se las pagas a Stripe según tu acuerdo con él.',
            ],
          },
          {
            title: 'Devolvemos nuestra comisión cuando se devuelve una venta',
            blocks: [
              'Si reembolsas una venta, entera o en parte, devolvemos nuestra comisión en la misma proporción. Si pierdes una disputa sobre una venta, también devolvemos nuestra comisión de esa venta. Una disputa abierta o que ganas no cambia la comisión. No devolvemos las tarifas que cobra Stripe; esas se rigen por los términos de Stripe.',
            ],
          },
          {
            title: 'Cambios en la comisión',
            blocks: [
              'Podemos cambiar las comisiones. Te avisaremos al menos 30 días antes de que un cambio surta efecto, y se aplicará solo a las ventas hechas después de esa fecha.',
            ],
          },
        ],
      },
      {
        title: 'Reembolsos y disputas',
        subs: [
          {
            title: 'Reembolsos',
            blocks: [
              'Tú reembolsas a tus compradores desde tu cuenta de Stripe, a tu criterio y conforme a la política que les muestras y a la ley. El importe reembolsado sale de tu cuenta, y Stripe no devuelve su tarifa de procesamiento en un reembolso. Cuando Stripe nos informa de un reembolso, enviamos al comprador un aviso con el importe devuelto.',
            ],
          },
          {
            title: 'Disputas y contracargos',
            blocks: [
              'Cuando un comprador disputa un pago con su banco, el importe y la tarifa de la disputa se descuentan de tu cuenta de Stripe. Eres responsable de responder a las disputas con las pruebas que Stripe pida dentro del plazo que te dé, que puede ser de una semana. Cuando sabemos de una disputa nueva, mostramos un aviso en tu panel y te enviamos un correo con el importe y, cuando Stripe lo da, el plazo para responder, aunque hayas apagado los avisos de venta. No podemos responder por ti y no respondemos si se pierde una disputa, ni siquiera si un aviso no te llega.',
            ],
          },
          {
            title: 'Saldos negativos',
            blocks: [
              'Si los reembolsos o las disputas dejan tu cuenta de Stripe con saldo negativo, eso se rige por tu acuerdo con Stripe. Eres responsable de cualquier importe que debas a Stripe o a un comprador.',
            ],
          },
          {
            title: 'Reembolsos o disputas excesivos',
            blocks: [
              'Si tus Sitios generan una tasa inusualmente alta de reembolsos, disputas o quejas, nosotros o Stripe podemos pedirte información, limitar o desactivar las ventas en tus Sitios o cerrar tu Cuenta.',
            ],
          },
        ],
      },
      {
        title: 'Productos digitales, servicios y reservas',
        subs: [
          {
            title: 'Productos digitales',
            blocks: [
              'Eres responsable del contenido de los productos digitales que vendes y de tener derecho a venderlos. Los ponemos a disposición de tus compradores a través de la Plataforma, pero no garantizamos la entrega más allá del funcionamiento normal de la Plataforma, y no respondemos del uso que hagan los compradores de lo que descargan.',
            ],
          },
          {
            title: 'Servicios y reservas',
            blocks: [
              'Si ofreces servicios o reservas, tú fijas tu disponibilidad, precios, depósitos y políticas de cancelación y de ausencia, y eres responsable de prestar el servicio y de los reembolsos que debas a tus clientes. Por tu cuenta, podemos enviar confirmaciones y recordatorios de reservas y, si lo conectas, crear eventos en tu Google Calendar. Las reservas son un compromiso tuyo con tu cliente, no nuestro.',
            ],
          },
          {
            title: 'Ventas recurrentes',
            blocks: [
              'Si vendes suscripciones u otros cobros recurrentes, eres responsable de informar con claridad de las condiciones, obtener cualquier autorización que la ley exija, enviar los avisos de renovación o cancelación exigidos y ofrecer una forma fácil de cancelar.',
            ],
          },
        ],
      },
      {
        title: 'Suspensión y fin',
        blocks: [
          'Podemos desactivar el pago en tus Sitios en cualquier momento si creemos que incumples estos términos, los Términos de servicio o la ley, si hay un riesgo para los compradores o la Plataforma, o si Stripe nos lo pide. Puedes desconectar Stripe en cualquier momento; las operaciones existentes, los comprobantes y los pagos a tu banco se quedan en tu cuenta de Stripe y siguen regidos por tu acuerdo con Stripe. Desconectar no cancela los reembolsos ni las disputas ya en curso ni tus obligaciones con tus compradores.',
        ],
      },
      {
        title: 'Responsabilidad',
        blocks: [
          'No respondemos de los servicios de Stripe, de retenciones o retrasos en tus pagos a tu banco, de los actos u omisiones de tus compradores ni de las pérdidas derivadas de tus ventas, reembolsos o disputas. La exclusión de garantías, los límites de responsabilidad y la indemnización de los Términos de servicio aplican a todo lo de estos Términos de ventas y pagos.',
        ],
      },
    ],
  },
}
